import {RootState} from '../State';
import {Action, StoreAPI, KeyedAction} from '../Store';
import {isEnterpriseEnv, isOauthEnabled} from '../../utils/processHelpers';

export interface AccountInfo {
    email?: string;
    firstName?: string;
    isAdmin: boolean;
    lastName?: string;
    org?: string;
    phone?: string;
    username?: string;
}

export interface AuthenticationEnvironment {
    enterprise: boolean;
    oauth: boolean;
}

export type AuthenticationState = Readonly<{
    loggedIn: boolean;
    account: AccountInfo;
    environment: AuthenticationEnvironment;
}>;

const defaultAccountInfo: AccountInfo = {
    isAdmin: false
};

const environment: AuthenticationEnvironment = {
    enterprise: isEnterpriseEnv(),
    oauth: isOauthEnabled()
};

export const AuthenticationInitialState: AuthenticationState = {
    loggedIn: false,
    account: defaultAccountInfo,
    environment
};

/**
 * Begin the login and application startup.
 */
export class StartAuthentication extends KeyedAction<RootState, 'authentication'> {
    public readonly type: string = '@@AUTHENTICATION/START';

    constructor() {
        super('authentication');
    }
}

/**
 * Switches a users logged in state and account details.
 * Setting a user to logged out restarts the application.
 */
export class Authenticated extends KeyedAction<RootState, 'authentication'> {
    public readonly type: string = '@@AUTHENTICATION/AUTHENTICATED';

    constructor(public readonly loggedIn: boolean, private readonly account: AccountInfo = defaultAccountInfo) {
        super('authentication');
        if (!loggedIn) {
            this.account = defaultAccountInfo;
        }
    }

    public async call(store: StoreAPI<RootState>, complete: () => Promise<void>): Promise<void> {
        await complete();
    }

    public reduce(state: AuthenticationState): AuthenticationState {
        const {loggedIn, account} = this;

        return {
            ...state,
            loggedIn,
            account
        };
    }
}

export class LaunchAdminWindow extends Action<RootState> {
    public readonly type: string = '@@AUTHENTICATION/LAUNCH_ADMIN_WINDOW';
}

export class PostLogin extends Action<RootState> {
    public readonly type: string = '@@AUTHENTICATION/POST_LOGIN';

    public async call(store: StoreAPI<RootState>, complete: () => Promise<void>): Promise<void> {
        await store.dispatch(new LaunchWindows());
        await complete();
    }

    public reduce(state: RootState): RootState {
        const {authentication} = state;
        return {
            ...state,
            authentication: {
                ...authentication,
                loggedIn: true
            }
        };
    }
}

export class Logout extends Action<RootState> {
    public readonly type: string = '@@AUTHENTICATION/LOGOUT';
}

/**
 * Signal to controllers that windows can be opened now.
 * This action should be awaited before showing the launchbar.
 */
export class LaunchWindows extends Action<RootState> {
    public readonly type: string = '@@AUTHENTICATION/LAUNCH_WINDOWS';
}
